import { useEffect, useState } from "react";
import { Star, PenLine, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const Stars = ({ value, size = 14, onPick = null }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <button key={i} type="button" disabled={!onPick} onClick={() => onPick?.(i)}
        data-testid={onPick ? `review-star-${i}` : undefined}
        className={onPick ? "cursor-pointer hover:scale-110 transition-transform" : "cursor-default"}>
        <Star size={size} strokeWidth={1.5}
          className={i <= Math.round(value) ? "text-[#B84A3B] fill-[#B84A3B]" : "text-[#B0B0A8]"} />
      </button>
    ))}
  </div>
);

const EMPTY_FORM = { rating: 5, title: "", body: "", name: "", email: "" };

export const ReviewSection = ({ product }) => {
  const [data, setData] = useState(null);
  const [writing, setWriting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setData(null); setWriting(false);
    api.get(`/reviews/list/${product.shopify_id}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ reviews: [], count: 0, rating: null }));
  }, [product.shopify_id]);

  const submit = async () => {
    if (form.name.trim().length < 2 || !form.email.includes("@") || form.body.trim().length < 10) {
      toast.error("Please add your name, email and a few words about the product.");
      return;
    }
    setSending(true);
    try {
      await api.post("/reviews", { product_id: product.shopify_id, ...form });
      toast.success("Thank you! Your review will appear after a quick check.");
      setWriting(false); setForm(EMPTY_FORM);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit right now — please try again.");
    } finally { setSending(false); }
  };

  const reviews = data?.reviews || [];
  const inputCls = "w-full border border-[#E5E5DF] px-4 py-3 text-sm font-body bg-white focus:border-[#1A1A1A] outline-none";

  return (
    <section className="mt-20 border-t border-[#E5E5DF] pt-14" data-testid="product-reviews">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Reviews</h2>
          {data && data.count > 0 && (
            <div className="flex items-center gap-2.5 mt-3">
              <Stars value={data.rating} size={16} />
              <span className="font-mono text-[0.72rem] text-[#6E6E69]" data-testid="reviews-summary">
                {data.rating} · {data.count} review{data.count > 1 ? "s" : ""}
              </span>
            </div>
          )}
        </div>
        <button onClick={() => setWriting(!writing)} data-testid="write-review-button"
          className="inline-flex items-center gap-2 border border-[#1A1A1A] px-5 py-3 text-[0.66rem] font-mono tracking-[0.18em] uppercase hover:bg-[#1A1A1A] hover:text-white transition-colors">
          {writing ? <><X size={14} /> Cancel</> : <><PenLine size={14} /> Write a review</>}
        </button>
      </div>

      {writing && (
        <div className="border border-[#E5E5DF] p-6 mb-10 space-y-4 max-w-2xl" data-testid="review-form">
          <div className="flex items-center gap-3">
            <span className="font-mono text-[0.66rem] tracking-wide uppercase text-[#6E6E69]">Your rating</span>
            <Stars value={form.rating} size={20} onPick={(v) => setForm({ ...form, rating: v })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input placeholder="Your name" value={form.name} data-testid="review-name"
              onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            <input placeholder="Email (never shown)" type="email" value={form.email} data-testid="review-email"
              onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
          </div>
          <input placeholder="Review title (optional)" value={form.title} data-testid="review-title"
            onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
          <textarea placeholder={`How did you like your ${product.title}?`} rows={4} value={form.body} data-testid="review-body"
            onChange={(e) => setForm({ ...form, body: e.target.value })} className={inputCls} />
          <button onClick={submit} disabled={sending} data-testid="review-submit"
            className="bg-[#1A1A1A] text-white px-8 py-3 text-[0.66rem] font-mono tracking-[0.18em] uppercase hover:bg-[#B84A3B] transition-colors disabled:opacity-50">
            {sending ? "Sending…" : "Submit review"}
          </button>
        </div>
      )}

      {data === null ? (
        <div className="h-24 bg-[#EFEFEA] animate-pulse" />
      ) : reviews.length === 0 ? (
        <p className="font-body text-sm text-[#6E6E69]" data-testid="no-reviews-note">
          No reviews yet — be the first to share your experience with this piece!
        </p>
      ) : (
        <div className="space-y-8 max-w-3xl" data-testid="review-list">
          {reviews.map((rv, i) => (
            <article key={i} className="border-b border-[#EFEFEA] pb-8" data-testid={`review-item-${i}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <Stars value={rv.rating} />
                <span className="font-body text-sm font-medium text-[#1A1A1A]">{rv.name}</span>
                {rv.date && <span className="font-mono text-[0.65rem] text-[#B0B0A8]">{rv.date}</span>}
              </div>
              {rv.title && <p className="font-body font-medium text-sm mt-2.5">{rv.title}</p>}
              <p className="font-body text-sm text-[#6E6E69] leading-relaxed mt-2">{rv.body}</p>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};
